import {kitFetch, kitFetchNotJson, kitGetBlob} from "@ekultur/fetch";
import camelcaseKeysDeep from "camelcase-keys-deep";
import {getSentryTraceHeaders} from "../getSentryTraceHeaders";
import {clientLog} from "../clientLog";
import {getBrowserLocale} from "../utility";

/**
 * Returns the base URL for the DAMS Admin API.
 * @returns {string}
 * @private
 */
const _getDamsUrl = () => {
    return window._env_.REACT_APP_DAMS_ADMIN_API;
};

/**
 * Gets the x-quote-mode value appended to each backend-request in order to
 * redirect to the correct backend instance.
 * @returns {string}
 */
export const getXQuoteMode = () => {
    return window._env_.REACT_APP_X_QUOTE_MODE;
};

/**
 * Creates an x-quote-mode header entry in the options object or adds it to an existing array of headers.
 * Also sends the UI language as Accept-Language, which the backend uses to sort titles and descriptions
 * in that language's alphabetical order (DE-1695). Headers the caller set are kept.
 * Non-mutating: always returns a new options object with merged headers.
 * @param options
 * @returns {{headers: object}}
 */
export const appendXQuoteModeHeader = (options = {}) => {
    const existingHeaders = options.headers || {};
    const headers = {
        ...existingHeaders,
        ...getSentryTraceHeaders(),
    };
    if (!("x-quote-mode" in headers)) {
        headers["x-quote-mode"] = getXQuoteMode();
    }
    if (!_hasHeader(headers, "accept-language")) {
        headers["Accept-Language"] = getBrowserLocale(true);
    }
    return {...options, headers};
};

/**
 * Whether a header is present, ignoring case as HTTP does.
 * @param {object} headers
 * @param {string} lowerCaseName
 * @returns {boolean}
 * @private
 */
const _hasHeader = (headers, lowerCaseName) => {
    return Object.keys(headers).some((name) => name.toLowerCase() === lowerCaseName);
};

export const damsFetch = async (url, options, noCamelCase = false) => {
    try {
        const data = await kitFetch(`${_getDamsUrl()}${url}`, appendXQuoteModeHeader(options));
        if (noCamelCase) {
            return data;
        } else {
            return camelcaseKeysDeep(data);
        }
    } catch (error) {
        clientLog('error', error, 'damsfetch');
        return Promise.reject(error);
    }
};

/**
 * Fetches a streaming (e.g. Server-Sent Events) endpoint and returns the raw
 * Response object. Uses kitFetchNotJson so the body is never auto-parsed as
 * JSON. Auth (Bearer) and x-quote-mode headers are still applied.
 *
 * @param {string} url     - Relative URL, e.g. '/share/'
 * @param {Object} options - Fetch options (method, headers, body, …)
 * @returns {Promise<Response>}
 */
export const damsStreamFetch = (url, options) => {
    return kitFetchNotJson(`${_getDamsUrl()}${url}`, appendXQuoteModeHeader(options));
};

export const damsSearch = (params) => {
    return damsFetch(`/search?${params.toString()}`);
};

/**
 * Fetches a PDF file from the DAMS Admin API as a binary blob.
 * @param {string} url - The endpoint URL to fetch the PDF from.
 * @param {Object} options - The options object for the request.
 * @returns {Promise<ArrayBuffer>} A promise that resolves to the PDF file as an ArrayBuffer.
 * @throws Will throw an error if the PDF fetch fails.
 */
export const damsFetchPDF = async (url, options) => {
    try {
        const pdfUrl = `${_getDamsUrl()}${url}`;
        const res = await kitGetBlob(pdfUrl, appendXQuoteModeHeader(options));
        if (!res.ok) {
            return Promise.reject('failed to fetch PDF');
        }
        return await res.arrayBuffer();
    } catch (error) {
        clientLog('error', error, 'damsfetchpdf');
        return Promise.reject('failed to fetch PDF');
    }
};

export const solrEscape = (q) => q.replace("(", "(").replace(")", ")");
